
import React, { useState } from 'react';
import { Card, Button, Input } from '../components/UI';
import { useLeague } from '../components/LeagueContext';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import * as ReactRouterDOM from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { PageHero } from '../components/PageHero';

const { useNavigate } = ReactRouterDOM;

const STEPS = ['Saisondaten', 'Strecken wählen', 'Kalender & Setup', 'Teams wählen', 'Prüfen'];

export const AdminWizard: React.FC = () => {
  const navigate = useNavigate();
  const { tracks, teams, refreshData } = useLeague();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State für die Saison-Daten
  const [seasonData, setSeasonData] = useState({
    name: '',
    year: new Date().getFullYear(),
    selectedTeams: [] as string[], // Team IDs
    selectedTracks: [] as string[], // Track IDs in Reihenfolge
    eventsConfig: {} as Record<string, { date: string, isSprint: boolean, distance: string }>
  });

  const nextStep = () => setCurrentStep(p => Math.min(STEPS.length - 1, p + 1));
  const prevStep = () => setCurrentStep(p => Math.max(0, p - 1));

  // --- Helpers for ordering ---
  const moveTrack = (index: number, direction: 'up' | 'down') => {
      const newTracks = [...seasonData.selectedTracks];
      if (direction === 'up' && index > 0) {
          [newTracks[index], newTracks[index - 1]] = [newTracks[index - 1], newTracks[index]];
      } else if (direction === 'down' && index < newTracks.length - 1) {
          [newTracks[index], newTracks[index + 1]] = [newTracks[index + 1], newTracks[index]];
      }
      setSeasonData({...seasonData, selectedTracks: newTracks});
  };

  const handleCreateSeason = async () => {
      setIsSubmitting(true);
      try {
          // Payload zusammenbauen
          // Wir iterieren über selectedTracks, damit die Reihenfolge im Kalender stimmt
          const eventsPayload = seasonData.selectedTracks.map(trackId => {
              const config = seasonData.eventsConfig[trackId] || { date: '', isSprint: false, distance: '50%' };
              // Default Datum: Jetzt + 7 Tage, wenn leer
              const eventDate = config.date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
              return {
                  trackId,
                  date: eventDate,
                  isSprint: config.isSprint,
                  distance: config.distance
              };
          });

          await apiRequest('seasons.php', 'POST', {
              name: seasonData.name,
              year: seasonData.year,
              teamIds: seasonData.selectedTeams,
              events: eventsPayload
          });

          await refreshData(); // Daten neu laden (Dashboard aktualisieren)
          alert('Season created successfully! You can now assign drivers.');
          navigate('/');
      } catch (e: any) {
          alert('Error creating season: ' + e.message);
      } finally {
          setIsSubmitting(false);
      }
  };

  const updateConfig = (trackId: string, key: string, val: any) => {
      const config = seasonData.eventsConfig[trackId] || { date: '', isSprint: false, distance: '50%' };
      setSeasonData(prev => ({
          ...prev,
          eventsConfig: {
              ...prev.eventsConfig,
              [trackId]: { ...config, [key]: val }
          }
      }));
  };

  // --- Main Render ---

  return (
    <div className="max-w-5xl mx-auto animate-fade-in space-y-6 pb-20">
      <PageHero
        eyebrow="Season Builder"
        title="Neue Saison"
        description="In fünf klaren Schritten Saisondaten, Strecken, Termine und teilnehmende Teams zusammenstellen."
        icon={CheckCircle}
        accent="#e10600"
        stats={[{ label: 'Schritt', value: `${currentStep + 1}/${STEPS.length}` }, { label: 'Strecken', value: seasonData.selectedTracks.length }, { label: 'Teams', value: seasonData.selectedTeams.length }]}
      />
      <div className="mb-8 rounded-[1.5rem] border border-white/[0.075] bg-[#0e1017] p-5">
        {/* Progress Bar */}
        <div className="flex items-center justify-between relative">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-800 -z-10"></div>
            {STEPS.map((step, idx) => {
                const isCompleted = idx < currentStep;
                const isCurrent = idx === currentStep;
                return (
                    <div key={idx} className="flex flex-col items-center gap-2 bg-f1-dark px-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-colors duration-300 ${isCurrent ? 'bg-f1-red text-white' : isCompleted ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-500'}`}>
                            {isCompleted ? <CheckCircle size={16} /> : idx + 1}
                        </div>
                        <span className={`text-xs font-bold uppercase ${isCurrent ? 'text-white' : 'text-gray-500'}`}>{step}</span>
                    </div>
                )
            })}
        </div>
      </div>

      <Card className="min-h-[400px] rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
        <div className="mb-6">
            <h2 className="text-xl font-bold uppercase border-b border-gray-800 pb-2">{STEPS[currentStep]}</h2>
        </div>
        
        {currentStep === 0 && (
            <div className="space-y-4 max-w-lg">
                <Input 
                    label="Saisonname" 
                    placeholder="z. B. SDC Saison 14" 
                    value={seasonData.name}
                    onChange={e => setSeasonData(prev => ({...prev, name: e.target.value}))}
                />
                <Input 
                    label="Jahr" 
                    type="number"
                    value={seasonData.year}
                    onChange={e => setSeasonData(prev => ({...prev, year: parseInt(e.target.value)}))}
                />
            </div>
        )}

        {currentStep === 1 && (
            <div className="space-y-2">
                <p className="text-sm text-gray-400 mb-2">Wähle die Strecken für diese Saison. Die Reihenfolge kann im nächsten Schritt angepasst werden.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-2">
                    {tracks.map(track => {
                        const isSelected = seasonData.selectedTracks.includes(track.id);
                        return (
                            <div 
                                key={track.id}
                                onClick={() => {
                                    const newTracks = isSelected 
                                        ? seasonData.selectedTracks.filter(id => id !== track.id)
                                        : [...seasonData.selectedTracks, track.id];
                                    setSeasonData({...seasonData, selectedTracks: newTracks});
                                }}
                                className={`cursor-pointer p-4 rounded border transition-all select-none ${isSelected ? 'bg-f1-red/10 border-f1-red' : 'bg-gray-800 border-gray-700 hover:border-gray-500'}`}
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-bold">{track.location}</span>
                                    {isSelected && <CheckCircle className="text-f1-red w-5 h-5"/>}
                                </div>
                                <div className="text-xs text-gray-400">{track.name}</div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )}

        {currentStep === 2 && (
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                <p className="text-sm text-gray-400 mb-4">Set date and format. Use arrows to change race order.</p>
                {seasonData.selectedTracks.map((trackId, idx) => {
                    const track = tracks.find(t => t.id === trackId);
                    const config = seasonData.eventsConfig[trackId] || { date: '', isSprint: false, distance: '50%' };
                    
                    return (
                        <div key={trackId} className="bg-gray-800 p-3 rounded flex flex-col md:flex-row gap-4 border border-gray-700 items-start md:items-center">
                            <div className="flex flex-col gap-1 items-center">
                                <button onClick={() => moveTrack(idx, 'up')} disabled={idx === 0} className="text-gray-500 hover:text-white disabled:opacity-30"><ArrowUp size={16}/></button>
                                <div className="w-6 h-6 bg-gray-700 flex items-center justify-center font-bold rounded text-xs">{idx + 1}</div>
                                <button onClick={() => moveTrack(idx, 'down')} disabled={idx === seasonData.selectedTracks.length - 1} className="text-gray-500 hover:text-white disabled:opacity-30"><ArrowDown size={16}/></button>
                            </div>
                            
                            <div className="flex-1 min-w-[150px]">
                                <div className="font-bold truncate">{track?.location}</div>
                                <div className="text-xs text-gray-500 truncate">{track?.name}</div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
                                <input 
                                    type="datetime-local" 
                                    className="bg-gray-900 border border-gray-700 rounded text-sm px-2 py-1 text-white flex-1 md:flex-none"
                                    value={config.date}
                                    onChange={e => updateConfig(trackId, 'date', e.target.value)}
                                />
                                <select 
                                    className="bg-gray-900 border border-gray-700 rounded text-sm px-2 py-1 text-white"
                                    value={config.distance}
                                    onChange={e => updateConfig(trackId, 'distance', e.target.value)}
                                >
                                    <option value="50%">50%</option>
                                    <option value="100%">100%</option>
                                </select>
                                <div 
                                    className={`cursor-pointer px-3 py-1 rounded border text-sm font-bold select-none ${config.isSprint ? 'bg-orange-500/20 border-orange-500 text-orange-500' : 'bg-gray-900 border-gray-700 text-gray-500'}`}
                                    onClick={() => updateConfig(trackId, 'isSprint', !config.isSprint)}
                                >
                                    SPRINT
                                </div>
                            </div>
                        </div>
                    )
                })}
                {seasonData.selectedTracks.length === 0 && <div className="text-gray-500 italic">No tracks selected in previous step.</div>}
            </div>
        )}

        {currentStep === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto pr-2">
                {teams.map(team => {
                    const isSelected = seasonData.selectedTeams.includes(team.id);
                    return (
                        <div 
                            key={team.id}
                            onClick={() => {
                                const newTeams = isSelected 
                                    ? seasonData.selectedTeams.filter(id => id !== team.id)
                                    : [...seasonData.selectedTeams, team.id];
                                setSeasonData({...seasonData, selectedTeams: newTeams});
                            }}
                            className={`cursor-pointer p-3 rounded border transition-all flex items-center gap-3 select-none ${isSelected ? 'bg-gray-800 border-white' : 'bg-gray-900 border-gray-800 hover:border-gray-600'}`}
                        >
                            <div className="w-1 h-8 rounded-full" style={{backgroundColor: team.color}}></div>
                            <div className="flex-1 font-bold uppercase">{team.name}</div>
                            {isSelected && <CheckCircle className="text-white w-5 h-5"/>}
                        </div>
                    )
                })}
            </div>
        )}

        {currentStep === 4 && (
            <div className="space-y-6">
                <div className="bg-gray-800/50 p-6 rounded border border-gray-700">
                    <h3 className="text-xl font-bold uppercase italic text-f1-red mb-4">{seasonData.name || 'Untitled Season'} ({seasonData.year})</h3>
                    
                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-gray-400 font-bold uppercase text-xs mb-2">Calendar</h4>
                            <div className="text-3xl font-mono font-bold">{seasonData.selectedTracks.length} <span className="text-sm text-gray-500">RACES</span></div>
                        </div>
                        <div>
                            <h4 className="text-gray-400 font-bold uppercase text-xs mb-2">Grid</h4>
                            <div className="text-3xl font-mono font-bold">{seasonData.selectedTeams.length} <span className="text-sm text-gray-500">TEAMS</span></div>
                        </div>
                    </div>
                </div>
                
                <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded flex gap-3">
                    <AlertTriangle className="text-yellow-500 shrink-0" />
                    <div className="text-sm text-yellow-100">
                        Clicking "Create Season" will save it as a <strong>DRAFT</strong>. You can then assign drivers in the Season Settings.
                    </div>
                </div>
            </div>
        )}

        <div className="mt-8 pt-4 border-t border-gray-800 flex justify-between">
            <Button 
                variant="secondary" 
                onClick={prevStep} 
                disabled={currentStep === 0}
                icon={ArrowLeft}
            >
                Back
            </Button>
            
            {currentStep === STEPS.length - 1 ? (
                 <Button 
                    variant="primary" 
                    onClick={handleCreateSeason}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <><Loader2 className="animate-spin mr-2"/> Creating...</> : <><CheckCircle className="mr-2"/> Create Season</>}
                </Button>
            ) : (
                <Button 
                    variant="primary" 
                    onClick={nextStep}
                >
                    Next Step <ArrowRight className="ml-2 w-4 h-4 inline" />
                </Button>
            )}
        </div>
      </Card>
    </div>
  );
};
